/**
 * カスタムアコーディオン（.product-form__custom-acordion）の開閉
 * ＋ サイズ表: data-size-chart を JS でテーブル化（.js-product-size-chart-table）
 * ＋ .c-acordion__table--size ＋ .table-wrapper のモバイル幅・横スクロール・SimpleBar
 */

const ACCORDION_TABLE_MOBILE_MAX = 740;

/** SimpleBar が head で未実行のときに備え待機 */
function whenSimpleBarAvailable(callback) {
  if (typeof window.SimpleBar === 'function') {
    callback();
    return;
  }
  window.addEventListener(
    'load',
    () => {
      if (typeof window.SimpleBar === 'function') callback();
    },
    { once: true }
  );
  let n = 0;
  const id = window.setInterval(() => {
    if (typeof window.SimpleBar === 'function') {
      window.clearInterval(id);
      callback();
    } else if (++n > 120) {
      window.clearInterval(id);
    }
  }, 25);
}

function destroySimpleBarOnWrapper(wrapper) {
  const SB = window.SimpleBar;
  if (!wrapper || !SB || !SB.instances) return;
  const inst = SB.instances.get(wrapper);
  if (inst && typeof inst.unMount === 'function') {
    inst.unMount();
  }
  wrapper.removeAttribute('data-simplebar');
  wrapper.removeAttribute('data-simplebar-auto-hide');
}

function mountSimpleBarOnWrapper(wrapper) {
  const SB = window.SimpleBar;
  if (!wrapper || !SB) return;
  if (SB.instances && SB.instances.has(wrapper)) {
    const inst = SB.instances.get(wrapper);
    if (inst && typeof inst.recalculate === 'function') {
      inst.recalculate();
    }
    return;
  }
  wrapper.setAttribute('data-simplebar', '');
  wrapper.setAttribute('data-simplebar-auto-hide', 'false');
  try {
    new SB(wrapper, { autoHide: false });
  } catch {
    // 二重初期化など
  }
}

/**
 * .c-acordion__table--size（各 table 要素）。モバイルで .table-wrapper に幅とスクロール、SimpleBar。
 */
function setAcordionTableWrappers(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const sizeTables = scope.querySelectorAll('.c-acordion__table--size');
  const screenWidth = document.documentElement.clientWidth;
  const isMobile = screenWidth <= ACCORDION_TABLE_MOBILE_MAX;
  /** @type {HTMLElement[]} */
  const wrappersToMount = [];

  sizeTables.forEach((table) => {
    const tableWrapper = table.closest('.table-wrapper');
    if (!tableWrapper) return;

    if (isMobile) {
      const containerGutter = getComputedStyle(document.documentElement).getPropertyValue('--container-gutter');
      const gutterValue = parseFloat(containerGutter) || 0;
      const calculatedWidth = screenWidth - gutterValue * 2;

      tableWrapper.style.overflowX = 'scroll';
      tableWrapper.style.overflowY = 'hidden';
      tableWrapper.style.width = `${calculatedWidth}px`;
      tableWrapper.style.maxWidth = `${calculatedWidth}px`;
      tableWrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');
      tableWrapper.style.setProperty('scrollbar-gutter', 'stable both-edges');

      table.style.minWidth = '600px';
      table.style.width = '600px';

      wrappersToMount.push(tableWrapper);
    } else {
      tableWrapper.style.overflowX = '';
      tableWrapper.style.overflowY = '';
      tableWrapper.style.width = '';
      tableWrapper.style.maxWidth = '';
      tableWrapper.style.removeProperty('-webkit-overflow-scrolling');
      tableWrapper.style.removeProperty('scrollbar-gutter');
      table.style.minWidth = '';
      table.style.width = '';

      destroySimpleBarOnWrapper(tableWrapper);
    }
  });

  if (wrappersToMount.length === 0) return;

  whenSimpleBarAvailable(() => {
    if (document.documentElement.clientWidth > ACCORDION_TABLE_MOBILE_MAX) return;
    wrappersToMount.forEach((wrapper) => mountSimpleBarOnWrapper(wrapper));
  });
}

/**
 * custom.product_size_chart を data-size-chart に載せた空 table を、改行=行・カンマ=列で生成。
 * 1行目 th、以降 td。処理済みは data-size-chart-built。
 */
function initProductSizeChartTables(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const tables = scope.querySelectorAll(
    'table.js-product-size-chart-table[data-size-chart]:not([data-size-chart-built])'
  );
  let built = false;

  tables.forEach((table) => {
    const raw = table.getAttribute('data-size-chart');
    if (raw == null || String(raw).trim() === '') return;

    const lines = String(raw)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length === 0) return;

    table.replaceChildren();
    const tbody = document.createElement('tbody');

    lines.forEach((line, rowIndex) => {
      const cells = line.split(',').map((cell) => cell.trim());
      const tr = document.createElement('tr');
      cells.forEach((cellText) => {
        const isHeader = rowIndex === 0;
        const cell = document.createElement(isHeader ? 'th' : 'td');
        if (isHeader) cell.setAttribute('scope', 'col');
        cell.textContent = cellText;
        tr.appendChild(cell);
      });
      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    table.setAttribute('data-size-chart-built', 'true');
    table.removeAttribute('data-size-chart');
    built = true;
  });

  if (built) {
    setAcordionTableWrappers(document);
  }
}

function runAcordionLayout(root = document) {
  initProductSizeChartTables(root);
  setAcordionTableWrappers(root);
}

function toggleCustomAcordion(button) {
  const container = button.closest('.c-acordion');
  if (!container || !container.closest('.product-form__custom-acordion')) return;

  const toggles = [...container.querySelectorAll('button.c-acordion__toggle')];
  const contents = [...container.querySelectorAll('.c-acordion__content')];
  const idx = toggles.indexOf(button);
  if (idx < 0) return;

  const willOpen = !button.classList.contains('is-active');

  toggles.forEach((toggle, i) => {
    const open = i === idx && willOpen;
    toggle.classList.toggle('is-active', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    const region = contents[i];
    if (!region) return;
    region.classList.toggle('is-open', open);
    if (open) {
      region.removeAttribute('hidden');
    } else {
      region.setAttribute('hidden', '');
    }
  });

  queueMicrotask(() => runAcordionLayout(document));
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('button.c-acordion__toggle');
  if (!toggle) return;
  toggleCustomAcordion(toggle);
});

runAcordionLayout(document);
window.addEventListener('resize', () => runAcordionLayout(document));

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target && typeof target.querySelectorAll === 'function') {
    runAcordionLayout(target);
  } else {
    runAcordionLayout(document);
  }
});

/** バリアント変更後の部分再描画のあとでもサイズ表・SimpleBar を再計算（theme.js の variant:change は bubbles） */
document.addEventListener('variant:change', () => {
  queueMicrotask(() => runAcordionLayout(document));
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => runAcordionLayout(document));
}
